import { useEffect, useState } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { listAccounts, listApps, registerApp, deleteApp, type PlayApp } from '@/api/play';
import { getApps } from '@/api/apps';
import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { Select } from '@/components/Select';
import { ConfirmModal } from '@/components/play/ConfirmModal';
import { ListingEditor } from '@/components/play/ListingEditor';
import { PlayTracksPanel } from '@/components/play/PlayTracksPanel';
import { GeoRatingsPanel } from '@/components/play/GeoRatingsPanel';
import { KeywordsPanel } from './play/KeywordsPanel';

export function PlayDetail() {
  const { accountId } = useParams();
  const accId = Number(accountId);
  const qc = useQueryClient();
  // ?app=<play_app_id> pre-selects an app — Update Batches links here.
  const [searchParams] = useSearchParams();
  const wanted = Number(searchParams.get('app'));
  const [selectedId, setSelectedId] = useState<number | null>(Number.isInteger(wanted) && wanted > 0 ? wanted : null);
  const [showRegister, setShowRegister] = useState(false);
  const [pkg, setPkg] = useState('');
  const [linkedAppId, setLinkedAppId] = useState('');
  const [registerError, setRegisterError] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);

  const { data: accounts = [] } = useQuery({ queryKey: ['play-accounts'], queryFn: listAccounts });
  const account = accounts.find((a) => a.id === accId);

  const { data: apps = [], isLoading, error, refetch } = useQuery({
    queryKey: ['play-apps', accId],
    queryFn: () => listApps(accId),
    enabled: Number.isFinite(accId),
  });
  const selected = apps.find((a) => a.id === selectedId) ?? null;

  const { data: dashboardApps = [] } = useQuery({
    queryKey: ['apps'],
    queryFn: getApps,
    enabled: showRegister,
  });

  useEffect(() => {
    // Also covers a ?app= id that is not on this account: fall back to the first app.
    if (apps.length > 0 && !apps.some((a) => a.id === selectedId)) setSelectedId(apps[0].id);
  }, [apps, selectedId]);

  // Auto-link the dashboard app whose bundle_id matches the typed package.
  useEffect(() => {
    if (!showRegister) return;
    const match = dashboardApps.find((a) => a.bundle_id === pkg.trim());
    if (match) setLinkedAppId(match.id);
  }, [showRegister, pkg, dashboardApps]);

  const registerMut = useMutation({
    mutationFn: () => registerApp(accId, { package_name: pkg.trim(), app_id: linkedAppId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['play-apps', accId] });
      setShowRegister(false);
      setPkg('');
      setLinkedAppId('');
      setRegisterError(null);
    },
    onError: (e: any) => setRegisterError(e?.message ?? 'Registration failed'),
  });

  const removeMut = useMutation({
    mutationFn: (app: PlayApp) => deleteApp(app.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['play-apps', accId] });
      setSelectedId(null);
      setConfirmRemove(false);
    },
  });

  return (
    <div className="p-6">
      <div className="flex flex-col xl:flex-row gap-6 items-start">
        <div className="flex-1 min-w-0 max-w-5xl">
      <div className="text-sm text-content-muted mb-1">
        <Link to="/play" className="text-accent hover:underline">Play Console</Link>
        {' / '}
        <span>{account?.name ?? `Account ${accountId}`}</span>
      </div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-semibold text-content">Managed apps</h1>
        <Button onClick={() => { setShowRegister(true); setRegisterError(null); }}>Register app</Button>
      </div>

      {error ? (
        <div className="bg-surface border border-border rounded-lg p-6 text-center">
          <p className="text-sm text-red-400 mb-3">{(error as Error).message}</p>
          <Button variant="secondary" size="sm" onClick={() => refetch()}>Retry</Button>
        </div>
      ) : isLoading ? (
        <p className="text-content-muted text-sm">Loading apps…</p>
      ) : apps.length === 0 ? (
        <div className="bg-surface border border-border rounded-lg p-8 text-center">
          <p className="text-sm text-content-muted">
            No apps registered yet. Register a package this account's service account can access.
          </p>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2 mb-4">
            <Select
              searchable
              className="w-96"
              value={selected ? String(selected.id) : ''}
              onChange={(v) => setSelectedId(Number(v))}
              options={apps.map((a) => ({
                value: String(a.id),
                label: `${a.last_error ? '⚠ ' : ''}${a.package_name}`,
              }))}
              placeholder="Select an app…"
            />
            {selected?.last_error && (
              <span className="text-xs text-red-400 max-w-[20rem] truncate" title={selected.last_error}>
                {selected.last_error}
              </span>
            )}
            {selected && (
              <Button variant="secondary" size="sm" className="ml-auto" onClick={() => setConfirmRemove(true)}>
                Remove app
              </Button>
            )}
          </div>
          {selected ? (
            <AppPanel app={selected} />
          ) : (
            <p className="text-content-muted text-sm">Select an app to manage its store listing and releases.</p>
          )}
        </>
      )}
        </div>
        {selected && <GeoRatingsPanel app={selected} />}
      </div>

      <Modal isOpen={showRegister} onClose={() => setShowRegister(false)} title="Register app">
        <p className="text-sm text-content-muted mb-3">
          The account's service account must already have access to this app — grant it in Play Console
          under “Users and permissions” first. Access is verified on registration.
        </p>
        <label className="block text-sm text-content-muted mb-1">Package name</label>
        <input
          className="w-full px-3 py-2 rounded-md bg-background border border-border text-content text-sm"
          value={pkg}
          onChange={(e) => setPkg(e.target.value)}
          placeholder="com.example.app"
        />
        <label className="block text-sm text-content-muted mb-1 mt-3">Dashboard app</label>
        <Select
          searchable
          value={linkedAppId}
          onChange={(v) => setLinkedAppId(v)}
          options={dashboardApps
            .filter((a) => a.status !== 'deleted')
            .map((a) => ({ value: a.id, label: `${a.name} (${a.bundle_id})` }))}
          placeholder="Select the app this package belongs to…"
        />
        {registerError && <p className="text-red-400 text-sm mt-2">{registerError}</p>}
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="secondary" onClick={() => setShowRegister(false)} disabled={registerMut.isPending}>Cancel</Button>
          <Button onClick={() => registerMut.mutate()} loading={registerMut.isPending} disabled={!pkg.trim() || !linkedAppId}>
            {registerMut.isPending ? 'Verifying access…' : 'Register'}
          </Button>
        </div>
      </Modal>

      <ConfirmModal
        isOpen={confirmRemove && !!selected}
        title="Remove app"
        danger
        confirmLabel="Remove"
        loading={removeMut.isPending}
        message={
          <>
            Remove <strong className="text-content">{selected?.package_name}</strong> from this dashboard?
            Nothing changes in Google Play — the app can be registered again later.
          </>
        }
        onConfirm={() => selected && removeMut.mutate(selected)}
        onClose={() => setConfirmRemove(false)}
      />
    </div>
  );
}

function AppPanel({ app }: { app: PlayApp }) {
  const [tab, setTab] = useState<'listing' | 'releases' | 'keywords'>('listing');
  return (
    <div>
      <div className="inline-flex gap-1 bg-surface border border-border rounded-lg p-1 mb-4">
        {(['listing', 'releases', 'keywords'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
              tab === t ? 'bg-primary text-white' : 'text-content-muted hover:text-content'
            }`}
          >
            {t === 'listing' ? 'Store listing' : t === 'releases' ? 'Releases' : 'Keywords'}
          </button>
        ))}
      </div>
      {tab === 'listing' ? (
        <ListingEditor app={app} />
      ) : tab === 'releases' ? (
        <PlayTracksPanel
          appId={app.id}
          appName={app.title ?? app.package_name}
          releaseNotesKeywords={app.release_notes_keywords}
        />
      ) : (
        <KeywordsPanel app={app} />
      )}
    </div>
  );
}
